// apps/client/src/state/slices/villagers.ts (M2-T11) — keys start with "villagers" (§8.7): the town's villagers and today's talk pauses
// (snapshot + villager.* events), and the open conversation. The talk's last line is the goodbye, held back until you say Bye.
import type { DialogueLine, FriendshipView, ItemId, Snapshot, VillagerId, VillagerPause, VillagerPublic } from '@whiskerton/shared';
import { getGame } from '../../game/instance';
import type { SliceCreator } from '../types';

export interface VillagersSlice {
  villagersList: VillagerPublic[]; villagersPauses: VillagerPause[];
  villagersTalk: { villager: VillagerId; convo: string; lines: DialogueLine[]; index: number; goodbye: DialogueLine | null } | null;
  villagersApplySnapshot(s: Snapshot): void;
  villagersUpsert(v: VillagerPublic): void; villagersRemove(id: VillagerId): void; villagersUpsertPause(p: VillagerPause): void;
  villagersStartTalk(id: VillagerId): Promise<boolean>; villagersGift(item: ItemId): Promise<boolean>;
  villagersEndTalk(): Promise<void>; villagersProtect(id: VillagerId, on: boolean): Promise<boolean>;
  villagersFriendship(id: VillagerId): FriendshipView | null;
}
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- declaration merging into GameStore
declare module '../types' { interface GameStore extends VillagersSlice {} }

export const slice: SliceCreator<VillagersSlice> = (set, get) => ({
  villagersList: [], villagersPauses: [], villagersTalk: null,
  villagersApplySnapshot: (s) => set({ villagersList: s.villagers, villagersPauses: s.pauses }),
  villagersUpsert: (v) => set((s) => ({ villagersList: [...s.villagersList.filter((x) => x.id !== v.id), v] })),
  villagersRemove: (id) => set((s) => ({ villagersList: s.villagersList.filter((x) => x.id !== id), villagersTalk: s.villagersTalk?.villager === id ? null : s.villagersTalk })),
  villagersUpsertPause: (p) => set((s) => ({ villagersPauses: [...s.villagersPauses.filter((x) => x.id !== p.id), p] })),
  async villagersStartTalk(id) {
    const res = await getGame().cmd.act('villager.talk', { villager: id });
    if (!res.ok) return false;
    const lines = res.r.lines;
    set({ villagersTalk: { villager: id, convo: res.r.convo, lines: lines.slice(0, -1), goodbye: lines.at(-1) ?? null, index: 0 } });
    return true;
  },
  async villagersGift(item) {
    const talk = get().villagersTalk;
    if (!talk) return false;
    const res = await getGame().cmd.act('villager.gift', { convo: talk.convo, item });
    if (!res.ok) return false;
    const now = get().villagersTalk;
    if (now?.convo === talk.convo) set({ villagersTalk: { ...now, lines: res.r.lines, index: 0 } });   // not if the talk ended meanwhile
    return true;
  },
  async villagersEndTalk() {
    const talk = get().villagersTalk;
    set({ villagersTalk: null });
    if (talk) await getGame().cmd.act('villager.talkEnd', { convo: talk.convo });
  },
  async villagersProtect(id, on) { return (await getGame().cmd.act('villager.protect', { villager: id, protect: on })).ok; },
  villagersFriendship: (id) => get().you?.friendships.find((f) => f.villager === id) ?? null,
});
